import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import styled from 'styled-components'

import FetchMore from 'js/components/FetchMore'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import NoResultsMessage from 'js/components/NoResultsMessage/NoResultsMessage'
import SearchBar from 'js/components/SearchBar/SearchBar'
import { useViewerStore } from 'js/lib/stores'
import WidgetTitle from 'js/page/Widget/components/components/WidgetTitle'
import { ExternalSearchSettings } from 'js/page/Widget/components/ExternalSearch/ExternalSearch'

import ExternalSearchResult from './components/ExternalSearchResult'
import EXTERNAL_SEARCH_QUERY from './externalSearchQuery'

const Wrapper = styled.div`
    .external-search--title {
        margin-bottom: 12px;
    }

    .external-search--input {
        margin-bottom: 20px;
    }
`

interface Props {
    guid: string
    settings: ExternalSearchSettings
}

const ExternalSearchView = ({ guid, settings }: Props) => {
    const {
        viewer: { canAccessExternalSearch },
    } = useViewerStore()

    const { t } = useTranslation()

    const [searchInput, setSearchInput] = useState('')
    const changeSearchInput = (evt) => setSearchInput(evt.target.value)

    const onSubmit = (evt) => {
        evt.preventDefault()
        search()
    }

    const [queryLimit, setQueryLimit] = useState(10)

    const [search, { loading, data, fetchMore }] = useLazyQuery(
        EXTERNAL_SEARCH_QUERY,
        {
            variables: {
                q: searchInput,
                offset: 0,
                limit: queryLimit,
            },
        },
    )

    if (!canAccessExternalSearch) {
        return null
    }

    const { title } = settings
    const total = data?.externalSearch?.total

    return (
        <Wrapper>
            <WidgetTitle
                title={title}
                widgetTitle={t('widget-external-search.title')}
                className="external-search--title"
            />

            <form method="GET" onSubmit={onSubmit}>
                <SearchBar
                    name={`${guid}-search-bar`}
                    value={searchInput}
                    onChange={changeSearchInput}
                    label={t('global.search')}
                    size="large"
                    className="external-search--input"
                />
            </form>

            {loading ? (
                <LoadingSpinner />
            ) : (
                <FetchMore
                    edges={data?.externalSearch?.edges || []}
                    getMoreResults={(data) => data?.externalSearch?.edges || []}
                    fetchMore={fetchMore}
                    fetchCount={10}
                    setLimit={setQueryLimit}
                    maxLimit={total}
                    resultsMessage={t('global.result', {
                        count: total,
                    })}
                    noResults={
                        <NoResultsMessage
                            title={t('search.no-results')}
                            subtitle={t('search.no-results-helper')}
                        />
                    }
                >
                    {data?.externalSearch?.edges?.map((result, i) => (
                        <ExternalSearchResult
                            key={result.id}
                            data-feed={i}
                            result={result}
                        />
                    ))}
                </FetchMore>
            )}
        </Wrapper>
    )
}

export default ExternalSearchView
